import { useEffect, useState, useCallback } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money } from "@/lib/api";
import { Plus, Trash } from "@phosphor-icons/react";

/**
 * Reusable P&L split editor. Lets a user release a single schedule across two or more
 * P&L codes, entered as percentages OR fixed amounts (converted to % of the total).
 * Emits onChange({ enabled, valid, pl_split: [{pl_account_code, percent}] }).
 */
export default function PLSplitEditor({ plAccounts = [], total = 0, primaryCode = "",
                                       initialSplit = null, onChange }) {
  const [enabled, setEnabled] = useState(Boolean(initialSplit && initialSplit.length >= 2));
  const [rows, setRows] = useState(() =>
    initialSplit && initialSplit.length >= 2
      ? initialSplit.map((s) => ({ code: s.pl_account_code, mode: "percent", value: String(s.percent) }))
      : [{ code: primaryCode || "", mode: "percent", value: "" },
         { code: "", mode: "percent", value: "" }]);

  const name = (code) => plAccounts.find((a) => a.code === code)?.name || "";

  const rowPercent = useCallback((r) => {
    const v = Number(r.value) || 0;
    if (r.mode === "amount") return total ? (v / total) * 100 : 0;
    return v;
  }, [total]);

  const rowAmount = (r) => {
    const v = Number(r.value) || 0;
    return r.mode === "amount" ? v : (total * v) / 100;
  };

  useEffect(() => {
    const norm = rows
      .filter((r) => r.code && Number(r.value) > 0)
      .map((r) => ({ pl_account_code: r.code, percent: Math.round(rowPercent(r) * 1e6) / 1e6 }));
    const sumPct = norm.reduce((a, b) => a + b.percent, 0);
    const codes = norm.map((n) => n.pl_account_code);
    const dup = new Set(codes).size !== codes.length;
    const valid = norm.length >= 2 && Math.abs(sumPct - 100) < 0.5 && !dup;
    onChange?.({ enabled, valid, pl_split: enabled ? norm : [], sumPct, dup });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, enabled, total]);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, { code: "", mode: "percent", value: "" }]);
  const removeRow = (i) => setRows((rs) => (rs.length > 2 ? rs.filter((_, k) => k !== i) : rs));

  const sumPct = rows.reduce((a, r) => a + (r.code && Number(r.value) > 0 ? rowPercent(r) : 0), 0);
  const codesUsed = rows.filter((r) => r.code && Number(r.value) > 0).map((r) => r.code);
  const dup = new Set(codesUsed).size !== codesUsed.length;

  return (
    <div className="md:col-span-2" data-testid="pl-split-editor">
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input type="checkbox" checked={enabled} data-testid="pl-split-toggle"
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-orange focus:ring-orange/40" />
        <span className="text-sm font-medium text-navy">Split releases across multiple P&amp;L codes</span>
      </label>

      {enabled && (
        <div className="mt-3 border border-slate-200 rounded-lg p-4 space-y-3 bg-navy/[0.02]">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`pl-split-row-${i}`}>
              <div className="flex-1">
                <Select value={r.code} onValueChange={(v) => setRow(i, { code: v })}>
                  <SelectTrigger data-testid={`pl-split-code-${i}`} className="rounded-lg border-slate-200 h-9">
                    <SelectValue placeholder="P&L code" />
                  </SelectTrigger>
                  <SelectContent>
                    {plAccounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex border border-slate-200 rounded-lg overflow-hidden text-xs shrink-0">
                {[{ v: "percent", l: "%" }, { v: "amount", l: "£" }].map((m) => (
                  <button key={m.v} type="button" data-testid={`pl-split-mode-${i}-${m.v}`}
                    onClick={() => setRow(i, { mode: m.v })}
                    className={`px-2.5 py-2 font-semibold transition-colors ${
                      r.mode === m.v ? "bg-orange text-white" : "bg-white text-navy/60 hover:bg-navy/5"}`}>
                    {m.l}
                  </button>
                ))}
              </div>
              <input type="number" min="0" step="0.01" value={r.value} data-testid={`pl-split-value-${i}`}
                onChange={(e) => setRow(i, { value: e.target.value })}
                placeholder={r.mode === "amount" ? "0.00" : "%"}
                className="w-24 shrink-0 bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
              <span className="w-24 shrink-0 text-right text-xs font-mono text-navy/60">
                {r.code && Number(r.value) > 0 ? money(rowAmount(r)) : "—"}
              </span>
              <button type="button" onClick={() => removeRow(i)} disabled={rows.length <= 2}
                data-testid={`pl-split-remove-${i}`}
                className="shrink-0 p-1.5 rounded-md text-navy/40 hover:text-negative hover:bg-negative/10 disabled:opacity-30 transition-colors">
                <Trash size={15} />
              </button>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1">
            <button type="button" onClick={addRow} data-testid="pl-split-add"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">
              <Plus size={14} weight="bold" /> Add code
            </button>
            <span data-testid="pl-split-total"
              className={`text-xs font-mono font-semibold ${Math.abs(sumPct - 100) < 0.5 && !dup ? "text-positive" : "text-negative"}`}>
              {dup ? "Duplicate P&L code" : `Total ${sumPct.toFixed(1)}% ${Math.abs(sumPct - 100) < 0.5 ? "✓" : "(must be 100%)"}`}
            </span>
          </div>
          <p className="text-xs text-navy/60">Each release journal debits/credits the split P&amp;L codes in these proportions; the balance-sheet side stays a single line.</p>
        </div>
      )}
    </div>
  );
}
